import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { toast } from 'sonner'

interface PushNotificationState {
  isSupported: boolean
  permission: NotificationPermission
  isSubscribed: boolean
  isLoading: boolean
}

export const usePushNotifications = () => {
  const { user } = useAuth()
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    isLoading: true
  })

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const isSupported = 'serviceWorker' in navigator && 
                         'PushManager' in window && 
                         'Notification' in window

      setState(prev => ({
        ...prev,
        isSupported,
        permission: isSupported ? Notification.permission : 'denied',
        isLoading: false
      }))
    }

    checkSupport()
  }, [])

  // Check existing subscription
  useEffect(() => {
    const checkSubscription = async () => {
      if (!state.isSupported || !user) return

      try {
        console.log('🔔 [Push] Verificando inscrição existente para usuário:', user.id)
        
        const registration = await navigator.serviceWorker.ready
        const subscription = await (registration as any).pushManager.getSubscription()
        
        console.log('🔔 [Push] Subscrição local encontrada:', !!subscription)
        
        // Se a subscrição local existe, verificar se está ativa no DB e reativar se necessário
        if (subscription) {
          const { data: dbSub } = await supabase
            .from('push_subscriptions')
            .select('is_active')
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint)
            .single()
          
          if (dbSub && !dbSub.is_active) {
            console.log('⚠️ [Push] Reativando subscrição inativa')
            await supabase
              .from('push_subscriptions')
              .update({ is_active: true })
              .eq('endpoint', subscription.endpoint)
            
            toast.success('Notificações reativadas automaticamente')
          }
        }
        
        setState(prev => ({
          ...prev,
          isSubscribed: !!subscription
        }))
      } catch (error) {
        console.error('❌ [Push] Erro ao verificar subscrição:', error)
      }
    }

    checkSubscription()
  }, [state.isSupported, user])

  const requestPermission = async (): Promise<boolean> => {
    if (!state.isSupported) {
      toast.error('Push notifications não são suportadas neste dispositivo')
      return false
    }

    try {
      const permission = await Notification.requestPermission()
      setState(prev => ({ ...prev, permission }))
      
      if (permission === 'granted') {
        toast.success('Permissão para notificações concedida!')
        return true
      } else {
        toast.error('Permissão para notificações negada')
        return false
      }
    } catch (error) {
      console.error('Error requesting permission:', error)
      toast.error('Erro ao solicitar permissão para notificações')
      return false
    }
  }

  const checkServiceWorkerStatus = async () => {
    if (!('serviceWorker' in navigator)) {
      console.error('❌ [Push] Service Worker não suportado')
      return false
    }
    
    let registration = await navigator.serviceWorker.getRegistration()
    if (!registration) {
      console.log('⚠️ [Push] Service Worker não registrado, tentando registrar...')
      try {
        registration = await navigator.serviceWorker.register('/service-worker.js')
        await navigator.serviceWorker.ready
        console.log('✅ [Push] Service Worker registrado com sucesso')
      } catch (err) {
        console.error('❌ [Push] Falha ao registrar Service Worker:', err)
        return false
      }
    }
    
    console.log('✅ [Push] Service Worker ativo:', registration.active?.state)
    return true
  }

  const subscribe = async (): Promise<boolean> => {
    if (!state.isSupported || !user) {
      console.error('❌ [Push] Subscrição não disponível - Suporte:', state.isSupported, 'User:', !!user)
      return false
    }

    console.log('🔔 [Push] Tentando inscrever usuário:', user.id)
    console.log('🔔 [Push] Permissão atual:', state.permission)

    // Verificar Service Worker
    const swReady = await checkServiceWorkerStatus()
    if (!swReady) {
      toast.error('Service Worker não está pronto')
      return false
    }

    if (state.permission !== 'granted') {
      const granted = await requestPermission()
      if (!granted) return false
    }

    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const registration = await navigator.serviceWorker.ready
      
      // Generate VAPID key (simplified - in production use proper VAPID keys)
      const vapidPublicKey = 'BEl62iUYgUivxIkv69yViEuiBIa40HI80NM9cC6xg_fKEJZrh4QVqJk1LfUYqaC8X1VRqQV3j9w7r0KJT_1p4-0'
      
      const subscription = await (registration as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      })

      console.log('✅ [Push] Subscrição criada:', {
        endpoint: subscription.endpoint.slice(-50),
        keys: !!subscription.toJSON().keys
      })

      // Save subscription to server
      const { data, error } = await supabase.functions.invoke('save-push-subscription', {
        body: { subscription: subscription.toJSON() }
      })

      if (error) {
        console.error('❌ [Push] Erro ao salvar subscrição:', error)
        toast.error('Erro ao registrar notificações')
        return false
      }

      console.log('✅ [Push] Subscrição salva no servidor:', data)

      setState(prev => ({ 
        ...prev, 
        isSubscribed: true,
        isLoading: false 
      }))
      
      toast.success('Notificações ativadas com sucesso!')
      return true

    } catch (error) {
      console.error('❌ [Push] Erro ao inscrever:', error)
      toast.error('Erro ao ativar notificações')
      setState(prev => ({ ...prev, isLoading: false }))
      return false
    }
  }

  const unsubscribe = async (): Promise<boolean> => {
    if (!state.isSupported || !user) {
      return false
    }

    console.log('🔔 [Push] Desativando notificações para usuário:', user.id)

    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const registration = await navigator.serviceWorker.ready
      const subscription = await (registration as any).pushManager.getSubscription()
      
      if (subscription) {
        await subscription.unsubscribe()
        console.log('✅ [Push] Subscrição local removida')
      }

      // Mark as inactive in database
      await supabase
        .from('push_subscriptions')
        .update({ is_active: false })
        .eq('user_id', user.id)

      setState(prev => ({ 
        ...prev, 
        isSubscribed: false,
        isLoading: false 
      }))
      
      toast.success('Notificações desativadas')
      return true

    } catch (error) {
      console.error('❌ [Push] Erro ao desativar:', error)
      toast.error('Erro ao desativar notificações')
      setState(prev => ({ ...prev, isLoading: false }))
      return false
    }
  }

  const sendTestNotification = async () => {
    if (!user) return

    console.log('🔔 [Push] Enviando notificação de teste para:', user.id)

    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          userIds: [user.id],
          title: 'Teste de Notificação',
          body: 'Esta é uma notificação de teste do itaCondo!',
          data: {
            url: '/',
            type: 'test'
          }
        }
      })

      if (error) {
        console.error('❌ [Push] Erro ao enviar teste:', error)
        toast.error('Erro ao enviar notificação de teste')
      } else {
        console.log('✅ [Push] Teste enviado:', data)
        toast.success('Notificação de teste enviada!')
      }
    } catch (error) {
      console.error('❌ [Push] Erro ao enviar teste:', error)
      toast.error('Erro ao enviar notificação de teste')
    }
  }

  return {
    ...state,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification
  }
}